import { useCallback, useEffect, useRef, useState } from 'react';
import { themeColor } from '../utils/theme';
import './PhotoCropModal.css';

/**
 * Square crop modal: drag to pan, range to zoom, Apply Crop / Use Original.
 */
export default function PhotoCropModal({ imageUrl, fileName = 'photo.jpg', onApply, onUseOriginal, onClose }) {
  const imgRef = useRef(null);
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);

  useEffect(() => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  }, [imageUrl]);

  const onImgLoad = () => {
    const img = imgRef.current;
    if (!img) return;
    setNatural({ w: img.naturalWidth, h: img.naturalHeight });
  };

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
  };

  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.x;
    const dy = e.clientY - dragRef.current.y;
    setOffset({ x: dragRef.current.ox + dx, y: dragRef.current.oy + dy });
  };

  const onPointerUp = () => {
    dragRef.current = null;
  };

  const exportCropped = useCallback(() => {
    const img = imgRef.current;
    if (!img || !natural.w) return;

    const out = 512;
    const canvas = document.createElement('canvas');
    canvas.width = out;
    canvas.height = out;
    const ctx = canvas.getContext('2d');

    const frame = 280;
    const scale = Math.max(frame / natural.w, frame / natural.h) * zoom;
    const drawW = natural.w * scale;
    const drawH = natural.h * scale;
    const dx = (frame - drawW) / 2 + offset.x;
    const dy = (frame - drawH) / 2 + offset.y;

    const sx = ((0 - dx) / scale);
    const sy = ((0 - dy) / scale);
    const sSize = frame / scale;

    ctx.fillStyle = themeColor('color-surface');
    ctx.fillRect(0, 0, out, out);
    ctx.drawImage(img, sx, sy, sSize, sSize, 0, 0, out, out);

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const base = (fileName || 'photo').replace(/\.[^.]+$/, '') || 'photo';
        const file = new File([blob], `${base}-cropped.jpg`, { type: 'image/jpeg' });
        onApply(file, URL.createObjectURL(blob));
      },
      'image/jpeg',
      0.92
    );
  }, [natural, zoom, offset, fileName, onApply]);

  const frame = 280;
  const scale =
    natural.w > 0 ? Math.max(frame / natural.w, frame / natural.h) * zoom : 1;
  const drawW = natural.w * scale;
  const drawH = natural.h * scale;

  return (
    <div className="crop-overlay" role="dialog" aria-modal="true" aria-label="Crop Your Photo">
      <div className="crop-modal">
        <h2 className="crop-title">Crop Your Photo</h2>
        <p className="crop-hint">Drag to adjust · use the slider to zoom</p>

        <div
          className="crop-frame"
          style={{ width: frame, height: frame }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <img
            ref={imgRef}
            src={imageUrl}
            alt=""
            onLoad={onImgLoad}
            draggable={false}
            style={{
              position: 'absolute',
              width: drawW || 'auto',
              height: drawH || 'auto',
              left: (frame - drawW) / 2 + offset.x,
              top: (frame - drawH) / 2 + offset.y,
              maxWidth: 'none',
              userSelect: 'none',
              pointerEvents: 'none',
            }}
          />
        </div>

        <label className="crop-zoom">
          Zoom
          <input
            type="range"
            min="1"
            max="3"
            step="0.05"
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
          />
        </label>

        <div className="crop-actions">
          <button type="button" className="crop-btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="crop-btn secondary" onClick={onUseOriginal}>
            Use Original
          </button>
          <button type="button" className="crop-btn primary" onClick={exportCropped}>
            Apply Crop
          </button>
        </div>
      </div>
    </div>
  );
}
